import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Modal, Button, Input, Textarea, Select, Label,
  StatusBadge, WeightBadge, TimelineBadge, TimelineStatusBadge, PriorityBadge, Avatar, UserSelect,
} from "@/components/UI";
import { STATUS_META, TASK_TYPE_META } from "@/lib/constants";
import { History, Info, MessageSquare, Pencil, Send, Trash2 } from "lucide-react";

export default function TaskModal({ open, onClose, task, mode = "view", onSaved, meta, quickMode = false, boardMode = false, urgentMode = false }) {
  const { isAdmin, user } = useAuth();
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState({});
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [phaseOptions, setPhaseOptions] = useState([]);
  const [editing, setEditing] = useState(false);
  const isCreate = mode === "create";

  useEffect(() => {
    if (!open) {
      setEditing(false);
      return;
    }
    setTab("details");
    const defaultType = quickMode ? "internal" : "project";
    const activeSprint = meta?.sprints?.find((s) => s.status === "active");
    setForm(
      task
        ? {
            title: task.title || "",
            description: task.description || "",
            type: task.type || (task.project_id ? "project" : "internal"),
            project_id: task.project_id || "",
            phase_id: task.phase_id || "",
            assignee_id: task.assignee_id || "",
            weight: task.weight ?? "",
            status: task.status || "backlog",
            start_date: task.start_date ? task.start_date.slice(0, 10) : "",
            deadline: task.deadline ? task.deadline.slice(0, 10) : "",
            urgency_reason: task.urgency_reason || "",
          }
        : {
            type: defaultType,
            status: "backlog",
            weight: "",
            ...(urgentMode
              ? {
                  is_urgent: true,
                  // Admin: urgent task langsung masuk sprint aktif sebagai To Do
                  ...(isAdmin ? { status: "to_do", sprint_id: activeSprint?.id ?? null } : {}),
                }
              : {}),
          }
    );
    if (task?.id && mode === "view") {
      api.get(`/tasks/${task.id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
    }
  }, [open, task, mode, isAdmin, quickMode, urgentMode, meta]);

  useEffect(() => {
    if (!form.project_id) {
      setPhaseOptions([]);
      return;
    }
    api.get(`/projects/${form.project_id}/phases`).then((r) => setPhaseOptions(r.data)).catch(() => setPhaseOptions([]));
  }, [form.project_id]);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "project_id") {
      if (v) next.type = "project";
      else next.type = "internal";
    }
    if (k === "type" && v !== "project") {
      next.project_id = "";
      next.phase_id = "";
    }
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.priority; // priority dihitung otomatis di backend dari deadline
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (isCreate) {
        await api.post("/tasks", payload);
        toast.success(isAdmin ? "Task dibuat" : "Urgent task dibuat — menunggu approval bobot");
      } else {
        await api.put(`/tasks/${task.id}`, payload);
        toast.success("Task diperbarui");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const selectedAssignee = (meta?.users || []).find((u) => String(u.id) === String(form.assignee_id));
  const isInternshipAssignee = selectedAssignee?.role === "internship";
  const canEditWeight = isAdmin && !isInternshipAssignee;
  const employeeCreate = isCreate && !isAdmin;
  const urgentCreate = isCreate && urgentMode;
  // Urgent task admin langsung aktif di sprint — bobot wajib diisi (kecuali assignee internship, bobot=0)
  const missingWeight = urgentCreate && isAdmin && !isInternshipAssignee && (form.weight === "" || form.weight == null);
  // Deadline ≤ 7 hari → otomatis urgent di backend; tampilkan field alasan urgent
  const deadlineUrgent = (() => {
    if (!form.deadline) return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return (new Date(form.deadline) - today) / 86400000 <= 7;
  })();
  const missingPhase = form.type === "project" && !!form.project_id && !form.phase_id;
  const missingProject = form.type === "project" && !form.project_id;

  const isEditForm = isCreate || editing;

  const footer = (
    <>
      <Button variant="ghost" onClick={() => (mode === "view" && editing ? setEditing(false) : onClose())} data-testid="task-modal-cancel">
        Batal
      </Button>
      {isEditForm && (
        <Button onClick={save} disabled={saving || !form.title || missingProject || missingPhase || missingWeight} data-testid="task-modal-save">
          {saving ? "Menyimpan..." : isCreate ? "Buat Task" : "Simpan"}
        </Button>
      )}
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isCreate ? (urgentMode || !isAdmin ? "Buat Urgent Task" : "Buat Task Baru") : task?.title}
      footer={isEditForm ? footer : null}
    >
      {mode === "view" && !editing ? (
        <ViewMode
          task={task}
          audit={audit}
          tab={tab}
          setTab={setTab}
          onSaved={onSaved}
          onClose={onClose}
          onEdit={() => setEditing(true)}
          isAdmin={isAdmin}
          user={user}
          boardMode={boardMode}
        />
      ) : (
        <div className="space-y-4">
          {employeeCreate && (
            <div className="flex gap-2 rounded-lg border border-slate-500/30 bg-slate-500/10 p-3 text-xs text-slate-200">
              <Info size={16} className="mt-0.5 shrink-0" />
              <span>
                Sebagai employee, Anda dapat membuat urgent task. Task akan berstatus{" "}
                <b>Waiting for Approval</b> dan <b>bobot ditentukan oleh Admin/PM</b>.
              </span>
            </div>
          )}

          {form.type === "internal" && (
            <div className="flex gap-2 rounded-lg border border-slate-700 bg-slate-800/40 p-3 text-xs text-slate-300">
              <Info size={16} className="mt-0.5 shrink-0 text-slate-400" />
              <span>
                Task ini <b>tidak terikat ke project</b> (jenis pekerjaan Internal). Gunakan ini untuk backlog umum
                yang belum masuk project tertentu.
              </span>
            </div>
          )}

          {form.type === "konsep" && (
            <div className="flex gap-2 rounded-lg border border-slate-500/30 bg-slate-500/10 p-3 text-xs text-slate-200">
              <Info size={16} className="mt-0.5 shrink-0" />
              <span>
                Task jenis <b>Konsep</b> — ide/eksplorasi yang belum jadi pekerjaan resmi. Tidak terikat project
                dan tidak masuk perhitungan sprint sampai diubah jenisnya.
              </span>
            </div>
          )}

          <div>
            <Label>Nama Task</Label>
            <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Contoh: Homepage hero section" data-testid="task-form-title" />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="task-form-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Jenis Pekerjaan</Label>
              <Select value={form.type || "project"} onChange={(e) => set("type", e.target.value)} data-testid="task-form-type" disabled={quickMode}>
                {Object.entries(TASK_TYPE_META).map(([value, meta]) => (
                  <option key={value} value={value}>{meta.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>PIC (Assignee)</Label>
              <UserSelect
                users={meta?.users || []}
                value={form.assignee_id || ""}
                onChange={(v) => set("assignee_id", v)}
                data-testid="task-form-assignee"
              />
            </div>
          </div>

          {form.type === "project" && (
            <>
              <div>
                <Label>Project</Label>
                <Select
                  value={form.project_id || ""}
                  onChange={(e) => set("project_id", e.target.value)}
                  data-testid="task-form-project"
                >
                  <option value="">— Pilih project —</option>
                  {meta?.projects?.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
              </div>

              {form.project_id && (
                <div>
                  <Label className="text-slate-400">Tahapan *</Label>
                  <Select value={form.phase_id || ""} onChange={(e) => set("phase_id", e.target.value)} data-testid="task-form-phase">
                    <option value="">— Pilih tahapan —</option>
                    {phaseOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Select>
                  {phaseOptions.length === 0 && (
                    <p className="mt-1 text-[11px] text-slate-400">Project ini belum punya tahapan. Tambahkan tahapan dulu di halaman detail project.</p>
                  )}
                </div>
              )}
            </>
          )}

          {!isInternshipAssignee && (
            <div>
              <Label className="text-slate-400">Bobot (pts){urgentCreate && isAdmin ? " *" : ""}</Label>
              <Input
                type="number"
                value={form.weight ?? ""}
                onChange={(e) => set("weight", e.target.value)}
                disabled={!canEditWeight}
                placeholder={canEditWeight ? "16" : "Admin only"}
                data-testid="task-form-weight"
                className={!canEditWeight ? "opacity-50" : ""}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400">Tanggal Mulai</Label>
              <Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="task-form-start-date" />
            </div>
            <div>
              <Label className="text-slate-400">Deadline</Label>
              <Input type="date" value={form.deadline || ""} onChange={(e) => set("deadline", e.target.value)} data-testid="task-form-deadline" />
            </div>
          </div>

          {urgentCreate && isAdmin && (
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-200">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!form.is_cleared}
                onChange={(e) => set("is_cleared", e.target.checked)}
                data-testid="task-form-cleared"
              />
              <span>
                <b>Cleared</b>
              </span>
            </label>
          )}

          {(urgentCreate || (!isCreate && deadlineUrgent)) && (
            <div>
              <Label>Alasan Urgent</Label>
              <Input value={form.urgency_reason || ""} onChange={(e) => set("urgency_reason", e.target.value)} data-testid="task-form-urgency" />
              <p className="mt-1 text-[11px] text-rose-400">
                {urgentCreate ? "Task ini dibuat sebagai URGENT." : "Deadline ≤ 7 hari — task ini otomatis berprioritas URGENT."}
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function ViewMode({ task, audit, tab, setTab, onSaved, onClose, onEdit, isAdmin, user, boardMode = false }) {
  const [status, setStatus] = useState(task.status);
  const [busy, setBusy] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [urlInput, setUrlInput] = useState(task.review_url || "");
  const [savingUrl, setSavingUrl] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isOwner = task.assignee_id === user?.id || task.created_by === user?.id;
  const canChange = isAdmin || isOwner;
  // Input URL review bisa diisi employee pemilik task maupun Admin/PM saat dibuka dari Kanban.
  const canEditUrl = canChange && boardMode;
  const editableStatuses = isAdmin
    ? ["backlog", "to_do", "in_progress", "review", "done", "on_hold"]
    : ["backlog", "to_do", "in_progress", "review"];
  const isPendingApproval = task.status === "waiting_approval";

  useEffect(() => {
    if (tab === "comments" && task?.id) {
      api.get(`/tasks/${task.id}/comments`).then((r) => setComments(r.data)).catch(() => {});
    }
  }, [tab, task?.id]);

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/tasks/${task.id}/comments`, { content: newComment });
      setComments((prev) => [r.data, ...prev]);
      setNewComment("");
      toast.success("Komentar ditambahkan");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menambahkan komentar");
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((prev) => prev.filter((c) => c.id !== id));
      toast.success("Komentar dihapus");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menghapus komentar");
    }
  };

  const changeStatus = async () => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}/status`, { status });
      toast.success("Status diperbarui");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal mengubah status");
    } finally {
      setBusy(false);
    }
  };

  const approveReview = async () => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}/status`, { status: "done" });
      toast.success("Review di-approve — task masuk Cleared");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal approve");
    } finally {
      setBusy(false);
    }
  };

  const toggleHold = async () => {
    setBusy(true);
    const toHold = task.status !== "on_hold";
    try {
      await api.patch(`/tasks/${task.id}/status`, { status: toHold ? "on_hold" : "to_do" });
      toast.success(toHold ? "Task ditunda (On Hold)" : "Task diaktifkan kembali");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal mengubah status");
    } finally {
      setBusy(false);
    }
  };

  const deleteTask = async () => {
    setBusy(true);
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task dihapus");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menghapus task");
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const saveUrl = async () => {
    setSavingUrl(true);
    try {
      await api.put(`/tasks/${task.id}`, { review_url: urlInput || null });
      toast.success("URL review disimpan");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menyimpan URL");
    } finally {
      setSavingUrl(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex gap-2 border-b border-slate-800">
        <button
          onClick={() => setTab("details")}
          className={`px-3 py-2 text-sm font-medium ${tab === "details" ? "border-b-2 border-slate-500 text-slate-400" : "text-slate-400"}`}
          data-testid="task-tab-details"
        >
          Detail
        </button>
        <button
          onClick={() => setTab("audit")}
          className={`flex items-center gap-1 px-3 py-2 text-sm font-medium ${tab === "audit" ? "border-b-2 border-slate-500 text-slate-400" : "text-slate-400"}`}
          data-testid="task-tab-audit"
        >
          <History size={14} /> Audit Trail
        </button>
        <button
          onClick={() => setTab("comments")}
          className={`flex items-center gap-1 px-3 py-2 text-sm font-medium ${tab === "comments" ? "border-b-2 border-slate-500 text-slate-400" : "text-slate-400"}`}
          data-testid="task-tab-comments"
        >
          <MessageSquare size={14} /> Komentar
        </button>
      </div>

      {tab === "details" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <StatusBadge status={task.status} />
              <TimelineStatusBadge timelineStatus={task.timeline_status} />
              <PriorityBadge priority={task.priority} />
              <TypeBadge type={task.type || (task.project_id ? "project" : "internal")} />
              {task.is_urgent && <span className="rounded-md bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-400">URGENT</span>}
            </div>
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={onEdit} data-testid="edit-task-button">
                <Pencil size={14} /> Edit Task
              </Button>
            )}
          </div>

          {task.description && <p className="text-sm leading-relaxed text-slate-300">{task.description}</p>}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MetaBox label="Bobot" accent="indigo"><WeightBadge weight={task.weight} /></MetaBox>
            <MetaBox label="Mulai" accent="sky"><TimelineBadge date={task.start_date} /></MetaBox>
            <MetaBox label="Deadline" accent="sky"><TimelineBadge date={task.deadline} /></MetaBox>
            <MetaBox label="Project">
              <span className="text-sm text-slate-200">{task.project?.name || "—"}</span>
            </MetaBox>
            <MetaBox label="Tahapan" accent="indigo">
              <span className="text-sm text-slate-200">{task.phase?.name || "—"}</span>
            </MetaBox>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/50 p-3">
            <Avatar src={task.assignee?.avatar_url} name={task.assignee?.name || "?"} size={34} />
            <div>
              <p className="text-sm font-medium text-slate-100">{task.assignee?.name || "Unassigned"}</p>
              <p className="text-xs text-slate-500">{task.assignee?.job_title || "PIC"}</p>
            </div>
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
            <Label className="text-slate-400">URL Review</Label>
            {task.review_url ? (
              <a href={task.review_url} target="_blank" rel="noreferrer"
                className="mb-2 block break-all text-sm text-slate-400 underline underline-offset-2"
                data-testid="task-review-url-link">
                {task.review_url}
              </a>
            ) : (
              <p className="mb-2 text-sm text-slate-500 italic" data-testid="task-review-url-empty">URL review tidak ada</p>
            )}
            {canEditUrl && (
              <div className="flex gap-2">
                <Input
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://... (link hasil kerja untuk review)"
                  data-testid="task-review-url-input"
                />
                <Button size="sm" onClick={saveUrl} disabled={savingUrl || urlInput === (task.review_url || "")} data-testid="task-review-url-save">
                  {savingUrl ? "..." : "Simpan"}
                </Button>
              </div>
            )}
          </div>

          {task.weight_approved === false && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200">
              Bobot belum di-approve. Menunggu Admin/PM menentukan bobot resmi.
            </div>
          )}

          {isAdmin && task.status === "review" && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-500/30 bg-slate-500/10 p-3">
              <p className="text-xs text-slate-200">
                Task menunggu review. Approve untuk memindahkan ke <b>Cleared</b>.
              </p>
              <Button size="sm" onClick={approveReview} disabled={busy} data-testid="approve-review-button">
                Approve & Cleared
              </Button>
            </div>
          )}

          {task.status === "on_hold" && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-500/30 bg-slate-500/10 p-3">
              <p className="text-xs text-slate-200">
                Task sedang <b>On Hold</b> — pekerjaan ditunda oleh Admin/PM.
              </p>
              {isAdmin && (
                <Button size="sm" onClick={toggleHold} disabled={busy} data-testid="resume-task-button">
                  Aktifkan Kembali
                </Button>
              )}
            </div>
          )}

          {isAdmin && !["on_hold", "done", "cancelled", "replaced", "waiting_approval"].includes(task.status) && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={toggleHold} disabled={busy} data-testid="hold-task-button">
                Tunda (On Hold)
              </Button>
            </div>
          )}

          {isAdmin && (
            confirmDelete ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3">
                <p className="text-xs text-rose-300">Yakin hapus task ini? Tidak bisa dibatalkan.</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>Batal</Button>
                  <Button variant="danger" size="sm" onClick={deleteTask} disabled={busy} data-testid="confirm-delete-task">
                    <Trash2 size={14} /> Ya, Hapus
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} data-testid="delete-task-button">
                  <Trash2 size={14} /> Hapus Task
                </Button>
              </div>
            )
          )}

          {canChange && !(boardMode && isAdmin) && !isPendingApproval && task.status !== "replaced" && task.status !== "cancelled" && (task.status !== "on_hold" || isAdmin) && (
            <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
              <Label>Quick Change Status</Label>
              <div className="flex gap-2">
                <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="quick-status-select">
                  {editableStatuses.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </Select>
                <Button onClick={changeStatus} disabled={busy || status === task.status} data-testid="quick-status-save">
                  Update
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : tab === "audit" ? (
        <div className="space-y-3" data-testid="task-audit-list">
          {audit.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Belum ada aktivitas.</p>}
          {audit.map((a) => (
            <div key={a.id} className="flex gap-3 border-l-2 border-slate-700 pl-3">
              <div className="flex-1">
                <p className="text-sm text-slate-200">
                  <span className="font-semibold capitalize text-slate-400">{a.action.replace("_", " ")}</span>
                  {a.field && <span className="text-slate-400"> · {a.field}</span>}
                  {a.old_value != null && a.new_value != null && (
                    <span className="font-mono text-xs text-slate-500"> {a.old_value} → {a.new_value}</span>
                  )}
                </p>
                {a.reason && <p className="text-xs italic text-slate-500">"{a.reason}"</p>}
                <p className="text-[11px] text-slate-600">
                  {a.actor?.name || "System"} · {new Date(a.created_at).toLocaleString("id-ID")}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3" data-testid="task-comments-list">
          <div className="space-y-2">
            <Textarea
              rows={2}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Tulis komentar..."
              data-testid="task-comment-input"
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={postComment} disabled={posting || !newComment.trim()} data-testid="task-comment-submit">
                <Send size={14} /> {posting ? "Mengirim..." : "Kirim"}
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            {comments.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Belum ada komentar.</p>}
            {comments.map((c) => (
              <div key={c.id} className="flex gap-3 rounded-lg border border-slate-800 bg-slate-950/30 p-3">
                <Avatar src={c.user?.avatar_url} name={c.user?.name || "?"} size={28} />
                <div className="flex-1">
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-200">{c.user?.name || "User"}</span>
                      <span className="text-[10px] text-slate-500">{new Date(c.created_at).toLocaleString("id-ID")}</span>
                    </div>
                    {(c.user_id === user?.id || isAdmin) && (
                      <button
                        onClick={() => deleteComment(c.id)}
                        className="text-slate-500 hover:text-rose-400"
                        title="Hapus komentar"
                        data-testid={`delete-comment-${c.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-slate-300">{c.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TypeBadge({ type }) {
  const meta = TASK_TYPE_META[type] || TASK_TYPE_META.project;
  return (
    <span
      className="rounded-md px-2 py-0.5 text-[11px] font-semibold"
      style={{ color: meta.color, backgroundColor: `${meta.color}15` }}
    >
      {meta.label}
    </span>
  );
}

function MetaBox({ label, children, accent }) {
  const colors = { indigo: "text-slate-400", sky: "text-slate-400", muted: "text-slate-400" };
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-2.5">
      <p className={`mb-1 text-[10px] uppercase tracking-wider ${colors[accent] || "text-slate-500"}`}>{label}</p>
      {children}
    </div>
  );
}
